import { type RefObject, useCallback, useEffect, useState } from "react";

import type { Confidence } from "@memorang/types";
import { useAilaStore } from "../../hooks/useAilaStore";
/* -----------------Hooks--------------- */
import useSessionStore from "../../hooks/useSessionStore";

import { events } from "@constants/tracking";
import { useDailyProgressStore } from "@features/dashboard";
import { useExamContext } from "@features/exam";
import { useProethicsFlowStore } from "@features/proethics-simulation";
import { useEMRStore } from "@features/session/components/emr-card/useEMRStore";
import {
	AI_SUBMITTABLE_VARIANTS,
	MAPPED_ITEM_TYPES,
} from "@features/session/constants";
import { isAiSimulationItem } from "@features/session/helpers";
import { getTotalScoreForAiItem } from "@features/session/helpers/aiFeedback";
import { isWeb } from "@helpers/platform";
import useAnalytics from "@hooks/useAnalytics";
import useClerkUser from "@hooks/useClerkUser";
import { useAuthContext } from "@memorang/applets";
import { Box } from "@memorang/ui";
import { router, useLocalSearchParams } from "expo-router";
import { useTranslation } from "react-i18next";
import DialogSession from "../../components/SessionDialog";
import SessionEndDialog from "../../components/SessionEndDialog";
/* -----------------Components--------------- */
import StudySessionBottomAppBar from "../../components/StudySessionBottomAppBar";
import type { DialogType } from "../../constants/dialog-configs";
import useAIFeedbackStore from "../../hooks/useAIFeedbackStore";
import { useSession } from "../../hooks/useSession";
import { recordSummativeTestEvents } from "../../relay/RecordSummativeTestEvents";
import {
	calculateAndUpdateProgress,
	getNextButtonStates,
	getShouldShowConfidencesSubmitButtons,
	getShowButtonNavFinish,
	getShowSubmitAnswerAIFeedbackButton,
	handlePostAnswer,
	handleSendSummativeAnswerEvent,
	hasAnySummativeSelection,
	sessionHasAnswerableItems,
} from "./helpers";

interface Props {
	// biome-ignore lint/suspicious/noExplicitAny: flashlistRef generic typing override
	flashlistRef?: RefObject<any>;
}
const StudySessionBottombarContainer = ({ flashlistRef }: Props) => {
	const state = useSessionStore((state) => state);

	const { index } = useLocalSearchParams<{
		index: string;
	}>();

	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const { viewerId } = useAuthContext();
	const {
		updateCurrentItemIndex,
		sessionItems,
		id,
		inReviewMode,
		isSummativeTest,
		currentBlockIndex,
		timeElapsed,
		sessionTime,
		updateProgress,
		numGoal,
		updateCurrentBlockIndex,
		numericId,
		toggleShowBreakDialog,
		answers,
	} = state;

	const { endCurrentSession } = useSession();

	const { explicitCallTrackCustomEvent } = useAnalytics();

	const hasAnsweredAtLeastOneItem = answers ? answers.size > 0 : false;
	const hasSummativeSelection = hasAnySummativeSelection(state);
	const hasNoAnswerableItems = !sessionHasAnswerableItems(state);

	const [startTime, setStartTime] = useState<Date>(new Date());
	const [showEndSessionDialog, setShowEndSessionDialog] = useState(false);

	const toggleShowEndSessionDialog = () => {
		setShowEndSessionDialog((prev) => !prev);
	};

	const dailyProgressStore = useDailyProgressStore();

	const [showSessionDialogData, setShowSessionDialogData] = useState<{
		show: boolean;
		type?: DialogType;
	}>({
		show: false,
	});

	const {
		currentExamContext: { examId },
	} = useExamContext();

	const updateShowDrawer = useAilaStore((state) => state.updateShowDrawer);

	const breaks = useSessionStore((state) => state.breaks);

	const lastBreak = breaks?.length ? breaks[breaks.length - 1] : null;

	const isBreakExpired = lastBreak?.status === "EXPIRED";

	const isBreakOngoing = useSessionStore((state) => state.isBreakOngoing);

	const currentSessionItems = sessionItems;
	const currentItem = currentSessionItems[currentItemIndex];

	const itemType = currentItem.metadata?.type;
	const mappedItemType = MAPPED_ITEM_TYPES[itemType!];

	const currentItemId = currentItem?.id!;
	const maximumScore =
		currentItem.response?.maximumScore ?? getTotalScoreForAiItem(itemType!);
	const isAiSubmittable = AI_SUBMITTABLE_VARIANTS.includes(mappedItemType);

	const { userPublicMetadataId } = useClerkUser();

	const slideshowDurationInSeconds = useAIFeedbackStore(
		(state) => state.slideshowDurationInSecondsByItemId[currentItemId],
	);

	const handleUpdateItemStatePeriodically = useCallback(
		(time: number) => {
			const itemState = {
				sessionId: `${id}`,
				sessionTime: time,
				itemId: `${currentItemId}`,
				userId: viewerId!,
				variant: itemType!,
			};
			recordSummativeTestEvents(itemState);
		},
		[currentItemId, id, viewerId, itemType],
	);

	useEffect(() => {
		if (
			timeElapsed &&
			timeElapsed % 300 === 0 &&
			!inReviewMode &&
			isSummativeTest &&
			!isBreakOngoing
		) {
			handleUpdateItemStatePeriodically(timeElapsed + (sessionTime || 0));
		}
	}, [
		handleUpdateItemStatePeriodically,
		inReviewMode,
		isSummativeTest,
		sessionTime,
		timeElapsed,
		isBreakOngoing,
	]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	useEffect(() => {
		setStartTime(new Date());
	}, [currentItemId]);

	const showConfidenceSubmitButtons =
		getShouldShowConfidencesSubmitButtons(state);

	// next button states
	const {
		text: nextButtonText,
		disabled: disableNextButton,
		showButtonNavSkip,
	} = getNextButtonStates(state, currentItemIndex);
	const showButtonNavFinish = getShowButtonNavFinish(state, currentItemIndex);

	const sessionLength = sessionItems.length;
	const isLastItem = currentItemIndex === sessionLength - 1;

	const { t } = useTranslation();

	const prevButtonText = t("session.navigation.previous");

	const showPreviousButton = currentItemIndex > 0;

	const isLastBlock = isSummativeTest
		? currentBlockIndex === sessionItems?.length - 1
		: false;

	const isLastItemInTheBlock = false;

	useEffect(() => {
		if (currentItemIndex === 0) {
			const initProgress = 1 / numGoal!;
			updateProgress(initProgress);
		}
	}, [currentItemIndex, numGoal, updateProgress]);
	const handleUpdateAnswers = (confidence: Confidence) => {
		handlePostAnswer(
			state,
			startTime,
			confidence,
			dailyProgressStore,
			examId,
			mappedItemType,
			userPublicMetadataId,
			!isAiSubmittable,
			undefined,
			undefined,
			slideshowDurationInSeconds,
		);
	};

	const handleTrackAnswerEventAi = (confidence: Confidence) => {
		const aiResponse =
			useAIFeedbackStore.getState().aiResponse?.[currentItemId];
		handlePostAnswer(
			state,
			startTime,
			confidence,
			dailyProgressStore,
			examId,
			mappedItemType,
			userPublicMetadataId,
			true,
			aiResponse,
			maximumScore,
			slideshowDurationInSeconds,
		);
	};

	const onConfidenceProvided = (confidence: Confidence) => {
		handleUpdateAnswers(confidence);
	};

	const handlePrevious = () => {
		explicitCallTrackCustomEvent({
			eventName: events.sessionItemNavigated,
			sessionId: id,
			itemId: currentItemId,
			type: "previous",
		});
		router.setParams({
			index: String(currentItemIndex),
			prev: "true",
		});
		updateCurrentItemIndex(currentItemIndex - 1);
		calculateAndUpdateProgress(state, false);
	};

	useEffect(() => {
		if (!isWeb) {
			flashlistRef?.current?.scrollToIndex({
				index: currentItemIndex,
				animated: true,
			});
		}
	}, [currentItemIndex, flashlistRef]);

	// ProEthics simulation gating: while the AI_SIMULATION item is current, Next
	// stays disabled until the learner ends the conversation. Ending freezes the
	// transcript; Next then fires the summative submit for the reflect step.
	const hasEndedSimulation = useProethicsFlowStore((s) => s.hasEnded);
	const submitSummative = useProethicsFlowStore((s) => s.submitSummative);
	const isSimulationItem = isAiSimulationItem(sessionItems[currentItemIndex]);

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const handleNext = () => {
		// Leaving the simulation: kick off the summative so the reflect step can
		// render the report (guarded against double submit inside the store).
		if (isSimulationItem) {
			submitSummative();
		}
		const eventParams = {
			index: currentItemIndex,
			itemId: currentItemId,
			sessionId: id,
			inReviewMode: String(inReviewMode),
		};
		if (isLastItemInTheBlock && !inReviewMode && isSummativeTest) {
			updateShowDrawer(false);
			const type = isLastBlock ? "completeTest" : "completeSection";
			if (isSummativeTest && !inReviewMode) {
				handleSendSummativeAnswerEvent(
					state,
					currentItemIndex,
					viewerId!,
					startTime,
				);
			}
			if (isBreakExpired) {
				setShowSessionDialogData({
					show: true,
					type,
				});
			} else {
				toggleShowBreakDialog(true);
			}
			return;
		}
		if (showButtonNavFinish || isLastItem) {
			updateShowDrawer(false);
			explicitCallTrackCustomEvent({
				eventName: events.sessionFinishNavClicked,
				...eventParams,
			});
			if (inReviewMode) {
				router.back();
			} else if (
				hasAnsweredAtLeastOneItem ||
				hasSummativeSelection ||
				hasNoAnswerableItems
			) {
				endCurrentSession({
					id: id,
					userId: userPublicMetadataId!,
				});
				explicitCallTrackCustomEvent({
					eventName: events.sessionItemNavigated,
					...eventParams,
					type: "finish",
				});
				router.replace({
					pathname: "/(protected)/session/[id]/report",
					params: {
						id: id,
					},
				});
			} else {
				toggleShowEndSessionDialog();
			}
			return;
		}

		const newIndex = currentItemIndex + 1;

		router.setParams({
			index: String(newIndex + 1),
			prev: "false",
		});
		updateCurrentItemIndex(newIndex);
		explicitCallTrackCustomEvent({
			eventName: events.sessionItemNavigated,
			...eventParams,
			type: "next",
		});
		if (isSummativeTest && !inReviewMode) {
			handleSendSummativeAnswerEvent(
				state,
				currentItemIndex,
				viewerId!,
				startTime,
			);
		}
		calculateAndUpdateProgress(state, true);
	};

	const progressText = isSummativeTest
		? ""
		: `ITEM ${currentItemIndex + 1} / ${sessionItems.length}`;

	const handleCloseDialog = () => {
		setShowSessionDialogData({
			show: false,
		});
	};
	const moveToNextBlock = () => {
		if (isLastBlock) {
			endCurrentSession({
				id: id,
				userId: userPublicMetadataId!,
			});
			router.replace({
				pathname: "/(protected)/session/[id]/report",
				params: {
					id: numericId,
				},
			});
		} else {
			router.setParams({
				index: "1",
			});
			updateCurrentItemIndex(1);
			updateCurrentBlockIndex(currentBlockIndex + 1);
		}
	};
	const handleClickActionButton = (type: string) => {
		if (type === "cancel") {
			handleCloseDialog();
		} else {
			handleCloseDialog();
			moveToNextBlock();
		}
	};

	const userResponse = useAIFeedbackStore((state) => state.userResponse);
	const audioResponse = useAIFeedbackStore((state) => state.audioResponse);
	const streaming = useAIFeedbackStore((state) => state.streaming);
	const { show: showSubmitAnswerAIFeedbackButton } =
		getShowSubmitAnswerAIFeedbackButton({
			sessionState: state,
			currentItemIndex,
			currentUserResponse: userResponse?.[currentItemId],
			currentAudioResponse: audioResponse?.[currentItemId],
			shouldValidateAudio: false,
		});

	const handleNavigation = (type: "positive" | "negative") => {
		if (type === "positive") {
			router.replace("/home");
		} else {
			toggleShowEndSessionDialog();
		}
	};

	const handleTrackConfidenceClick = (confidence: Confidence) => {
		explicitCallTrackCustomEvent({
			eventName: events.sessionItemConfidenceClicked,
			confidence,
			itemId: currentItemId,
			sessionId: id,
		});
	};

	const isSubmittingEMRData = useEMRStore((state) => state.isSubmittingEMRData);
	return (
		<Box maxWidth={"100%"} alignSelf="center" width="100%">
			<StudySessionBottomAppBar
				handleNext={handleNext}
				disableNextButton={
					disableNextButton ||
					streaming ||
					isSubmittingEMRData ||
					(isSimulationItem && !hasEndedSimulation)
				}
				disabledNextTooltip={
					isSimulationItem && !hasEndedSimulation
						? "The conversation is still active. End the conversation to continue."
						: undefined
				}
				nextButtonText={
					streaming ? t("session.navigation.next") : nextButtonText
				}
				handlePrevious={handlePrevious}
				handleTrackConfidenceClick={handleTrackConfidenceClick}
				disablePreviousButton={streaming}
				showSubmitAnswerAIFeedbackButton={showSubmitAnswerAIFeedbackButton}
				isSummativeTest={isSummativeTest}
				showButtonNavSkip={showButtonNavSkip && !streaming && !isSimulationItem}
				showButtonPrevious={showPreviousButton}
				prevButtonText={prevButtonText}
				showButtonNavFinish={showButtonNavFinish}
				onConfidenceProvided={onConfidenceProvided}
				showConfidenceSubmitButtons={Boolean(showConfidenceSubmitButtons)}
				progressText={progressText}
				onAiFeedbackProvided={handleTrackAnswerEventAi}
			/>

			{showSessionDialogData.show && (
				<DialogSession
					type={showSessionDialogData.type}
					handleClickButton={handleClickActionButton}
					handleClose={handleCloseDialog}
					open={showSessionDialogData.show}
				/>
			)}

			{showEndSessionDialog && (
				<SessionEndDialog
					visible={showEndSessionDialog}
					onDismiss={toggleShowEndSessionDialog}
					handleAction={handleNavigation}
					hasAnsweredAtLeastOneItem={false}
				/>
			)}
		</Box>
	);
};

export default StudySessionBottombarContainer;
